"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { MessageCircle, UsersRound } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import {
  companionSectionLoadFailedEvent,
  companionSectionRetryEvent,
} from "@/lib/companion/analytics-events";
import { CompanionDayIntentControls } from "../CompanionDayIntentControls";
import type { CompanionPeoplePulseModel } from "../people-pulse";
import { CompanionSectionHeading } from "./CompanionSectionHeading";

export function CompanionPeoplePulse({
  model,
}: {
  model: CompanionPeoplePulseModel;
}) {
  const router = useRouter();

  function retryPeople() {
    const event = companionSectionRetryEvent("people");
    try {
      trackEvent(event.name, event.properties);
    } catch {
      // Analytics must never prevent the section refresh.
    }
    router.refresh();
  }

  return (
    <section
      aria-labelledby="companion-people-pulse-heading"
      className="rounded-lg border border-border-light bg-white p-4 md:p-5"
    >
      {model.state === "partial" || model.state === "error" ? (
        <CompanionPeopleLoadFailureAnalytics
          unavailableSourceCount={model.unavailableLabels.length}
        />
      ) : null}
      <CompanionSectionHeading
        id="companion-people-pulse-heading"
        kicker="People"
        title={model.heading}
      />

      {model.state === "error" ? (
        <div className="mt-4 rounded-sm border border-terracotta/45 bg-terracotta/10 p-4">
          <p
            role="status"
            aria-live="polite"
            className="text-[13px] leading-relaxed text-navy"
          >
            People updates are unavailable. Your recommendation is still ready.
          </p>
          <button
            type="button"
            onClick={retryPeople}
            className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-sm border border-terracotta/55 bg-white px-4 py-2 text-[13px] font-semibold text-navy hover:border-terracotta"
          >
            Try again
          </button>
        </div>
      ) : model.state === "empty" ? (
        <div className="mt-4">
          <p className="text-[13.5px] leading-relaxed text-text-muted">
            Today works privately too. Create a Squad or find a Crew whenever
            you want company.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {model.emptyActions.map((action) => (
              <Link
                key={action.href}
                href={action.href}
                className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-warm-bg px-4 py-2 text-[13px] font-semibold text-navy no-underline hover:border-gold/50"
              >
                {action.label}
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {model.state === "ready" ? (
            <p className="text-[13px] leading-relaxed text-text-muted">
              {model.summary}
            </p>
          ) : null}

          {model.todayIntent?.placeSlug ? (
            <div data-testid="today-intent-controls">
              <CompanionDayIntentControls
                date={model.todayIntent.id}
                placeSlug={model.todayIntent.placeSlug}
                placeName={model.todayIntent.placeName}
                summary={model.todayIntent.intentSummary}
              />
            </div>
          ) : null}

          {model.crews.length > 0 ? (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-muted">
                Crews
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {model.crews.slice(0, 2).map((crew) => (
                  <Link
                    key={crew.id}
                    href={crew.href}
                    className="flex min-h-[52px] items-center gap-3 rounded-sm border border-border-light bg-warm-bg px-3 py-2 no-underline hover:border-gold/50"
                  >
                    <MessageCircle
                      className="h-4 w-4 shrink-0 text-gold"
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-semibold text-navy">
                        {crew.name}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-charcoal">
                        {crew.unread ? "New Crew activity" : "Crew space ready"}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ) : null}

          {model.squads.length > 0 ? (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-muted">
                Squads
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {model.squads.slice(0, 2).map((squad) => (
                  <div
                    key={squad.id}
                    className="flex min-h-[52px] items-center gap-3 rounded-sm border border-border-light bg-warm-bg px-3 py-2"
                  >
                    <UsersRound
                      className="h-4 w-4 shrink-0 text-gold"
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-semibold text-navy">
                        {squad.name}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-charcoal">
                        {squad.memberCount} {squad.memberCount === 1 ? "member" : "members"}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {model.state === "partial" ? (
            <p
              data-testid="people-partial-notice"
              role="status"
              className="rounded-sm border border-gold/35 bg-gold/10 px-3 py-2 text-[12px] leading-relaxed text-navy"
            >
              {model.unavailableLabels.length > 0
                ? `Some People updates are unavailable: ${model.unavailableLabels.join(", ")}.`
                : "Some People updates are unavailable."}
            </p>
          ) : null}
        </div>
      )}

      <Link
        href={model.primaryAction.href}
        className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-4 py-2 text-[13px] font-semibold text-white no-underline hover:bg-teal-dark"
      >
        {model.primaryAction.label}
      </Link>
    </section>
  );
}

export function CompanionPeopleLoadFailureAnalytics({
  unavailableSourceCount,
}: {
  unavailableSourceCount: number;
}): null {
  const trackedRef = useRef(false);

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;

    try {
      const event = companionSectionLoadFailedEvent(
        "people",
        unavailableSourceCount,
      );
      trackEvent(event.name, event.properties);
    } catch {
      // Analytics must never interrupt People rendering.
    }
  }, [unavailableSourceCount]);

  return null;
}

export function CompanionPeoplePulseLoading() {
  return (
    <section
      aria-labelledby="companion-people-pulse-loading-heading"
      className="rounded-lg border border-border-light bg-white p-4 md:p-5"
    >
      <CompanionSectionHeading
        id="companion-people-pulse-loading-heading"
        kicker="People"
        title="Your people today"
      />
      <div
        role="status"
        aria-live="polite"
        className="mt-4 space-y-2"
      >
        <span className="sr-only">Loading people updates</span>
        {[0, 1].map((row) => (
          <div
            key={row}
            data-testid="people-loading-row"
            className="h-[52px] rounded-sm border border-border-light bg-warm-bg motion-safe:animate-pulse"
          />
        ))}
      </div>
    </section>
  );
}
